import * as React from 'react';
import { useCallback, useRef } from 'react';
import { FaKeyboard } from 'react-icons/fa6';

import { useVirtualKeyboard } from '@/components/VirtualKeyboard';
import { cn } from '@/utils';

interface SimpleKeyboardTextareaProperties extends React.ComponentProps<'textarea'> {
  name: string;
  error?: boolean;
  showKeyboardIcon?: boolean; // Override to explicitly show/hide keyboard icon
}

const SimpleKeyboardTextarea = React.forwardRef<
  HTMLTextAreaElement,
  SimpleKeyboardTextareaProperties
>(({ className, name, disabled, error = false, showKeyboardIcon, ...properties }, reference) => {
  const internalReference = useRef<HTMLTextAreaElement>(null);
  const textareaReference =
    (reference as React.RefObject<HTMLTextAreaElement>) || internalReference;

  const virtualKeyboard = useVirtualKeyboard();

  const isTouchDevice = virtualKeyboard?.isTouchDevice ?? false;

  // Determine if we should show the keyboard icon
  // Show if: explicitly enabled OR (on touch device AND not disabled AND not explicitly disabled)
  const shouldShowKeyboardIcon =
    showKeyboardIcon === undefined
      ? isTouchDevice && !disabled
      : showKeyboardIcon && !disabled && isTouchDevice;

  const onKeyboardIconClick = useCallback(
    (event_: React.MouseEvent) => {
      event_.preventDefault();
      event_.stopPropagation();

      if (textareaReference.current && virtualKeyboard) {
        virtualKeyboard.triggerKeyboardForInput(textareaReference.current);
      }
    },
    [virtualKeyboard, textareaReference],
  );

  const onMouseDown = useCallback((event_: React.MouseEvent) => {
    event_.preventDefault();
    event_.stopPropagation();
  }, []);

  return (
    <div className="relative w-full">
      {shouldShowKeyboardIcon && (
        <button
          type="button"
          data-keyboard-trigger="true"
          className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-md p-1.5 transition-opacity hover:bg-gray-100 hover:opacity-70"
          onClick={onKeyboardIconClick}
          onMouseDown={onMouseDown}
          tabIndex={-1}
          aria-label="Open virtual keyboard"
        >
          <FaKeyboard className="h-5 w-5" />
        </button>
      )}
      <textarea
        name={name}
        disabled={disabled}
        maxLength={6400}
        className={cn(
          'border-primary custom-scrollbar-grey custom-scrollbar-grey-margin file:text-foreground placeholder:text-text-placeholder placeholder:font-poppins flex w-full overflow-x-hidden overflow-y-scroll rounded-lg border bg-white p-3 text-base leading-none shadow-sm transition-colors file:border-0 file:bg-white file:text-sm file:font-medium placeholder:font-medium focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-gray-200',
          className,
        )}
        style={{
          ...(error && { color: '#EB5757' }),
        }}
        ref={textareaReference}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...properties}
      />
    </div>
  );
});

SimpleKeyboardTextarea.defaultProps = {
  error: false,
  showKeyboardIcon: undefined,
};

export default SimpleKeyboardTextarea;
